import anime from "animejs";
import { Settings } from "./settings";
import { FEATURES_CONFIG, WIN_TYPES } from "./featuresConfig";

export const animateTextCounter = (
  targetRef,
  startValue,
  endValue,
  duration,
  onUpdate,
  onComplete
) => {
  if (!targetRef.current) return () => {};

  const obj = { value: startValue };

  const animation = anime({
    targets: obj,
    value: endValue,
    duration: duration,
    easing: "linear",
    update: () => {
      if (targetRef.current && onUpdate) {
        onUpdate(Math.floor(obj.value));
      }
    },
    complete: () => {
      if (targetRef.current && onComplete) {
        onComplete();
      }
    },
  });

  // Return cancel function
  return () => {
    animation.pause();
    if (onComplete) onComplete();
  };
};

/**
 * Format currency value with dynamic configuration
 *
 * @example
 * // Format with default config (100 subunits, 2 decimals)
 * formatCurrency(1000, { subunits: 100, exponent: 2 }, "en-US") // "10.00"
 */
export const formatCurrency = (value, config = {}, locale = "en-US") => {
  const { subunits = 100, exponent = 2 } = config;

  // Convert from subunits to main currency units
  const mainValue = value / subunits;

  // Note: toLocaleString() natively handles invalid locales by falling back to the default locale
  // No explicit validation needed - the browser/Node.js handles this gracefully
  return mainValue.toLocaleString(locale, {
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  });
};

/**
 * Format currency value with dynamic configuration from Redux store
 *
 * @example
 * // Simple usage - no config needed
 * const formatted = formatToCustomLocale(1000); // "10.00" (or "10,00" for de-DE locale)
 */
export const formatToCustomLocale = (value) => {
  // Import store dynamically to avoid circular dependencies
  // Store is available globally after app initialization
  let currencyConfig = { subunits: 100, exponent: 2 }; // Defaults
  let locale = "en-US"; // Default locale

  try {
    // Try to get currency config from Redux store
    // This works because the store is a singleton and available globally
    const store = require("./State/Store/store").default;
    const state = store.getState();

    // Get currency config from state if available
    if (state?.game?.currencyConfig) {
      currencyConfig = state.game.currencyConfig;
    }

    // Get locale from window options if available
    if (window.__OPTIONS__?.locale) {
      locale = window.__OPTIONS__.locale;
    }
  } catch (error) {
    // If store access fails (e.g., during tests or before initialization),
    // use defaults silently
    console.warn("Could not access Redux store for currency config, using defaults", error);
  }

  // Use the formatCurrency function with retrieved config
  return formatCurrency(value, currencyConfig, locale);
};

// Enhanced responsive font sizing with multiple breakpoints
export const calculateResponsiveFontSize = (width, sizes) => {
  // Default sizes if not provided
  const defaultSizes = {
    mobile: 24, // < 667px
    tablet: 32, // 667px - 1024px
    desktop: 48, // 1024px - 1280px
    large: 56, // ≥ 1280px
  };

  const fontSizes = { ...defaultSizes, ...sizes };

  if (width < 667) {
    return fontSizes.mobile;
  } else if (width < 1024) {
    return fontSizes.tablet;
  } else if (width < 1280) {
    return fontSizes.desktop;
  } else {
    return fontSizes.large;
  }
};

// Fullscreen utilities
export const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

export const isFullscreenSupported = () => {
  return (
    !isIOS() &&
    (document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled)
  );
};

export const isInFullscreen = () => {
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );
};

// Add fullscreen change event listeners
export const addFullscreenChangeListener = (callback) => {
  const events = [
    "fullscreenchange",
    "webkitfullscreenchange",
    "mozfullscreenchange",
    "MSFullscreenChange",
  ];

  events.forEach((event) => {
    document.addEventListener(event, callback);
  });

  // Return cleanup function
  return () => {
    events.forEach((event) => {
      document.removeEventListener(event, callback);
    });
  };
};

/**
 * Get win type based on win amount and bet size using centralized thresholds
 * @param {number} winAmount - Win amount in currency units
 * @param {number} betSize - Bet size in currency units
 * @param {string} roundType - Round type (e.g., "feature", "regular")
 * @returns {string} - Win type string that can be used for both display and sound
 */
export const getWinType = (winAmount, betSize = null, roundType = null) => {
  // Return empty string for invalid inputs
  if (!winAmount || winAmount <= 0 || !betSize || betSize <= 0) {
    return "";
  }

  const multiplier = winAmount / betSize;

  // Check thresholds from highest to lowest for cleaner code
  if (multiplier >= FEATURES_CONFIG.WIN_THRESHOLDS.SUPER_MEGA_WIN_THRESHOLD) {
    return WIN_TYPES.SUPER_MEGA_WIN;
  }

  if (multiplier >= FEATURES_CONFIG.WIN_THRESHOLDS.MEGA_WIN_THRESHOLD) {
    return WIN_TYPES.MEGA_WIN;
  }

  if (multiplier >= FEATURES_CONFIG.WIN_THRESHOLDS.BIG_WIN_THRESHOLD) {
    return WIN_TYPES.BIG_WIN;
  }

  // Below 20x is just a regular win (no special type)
  if (multiplier >= 0.1) {
    // Minimum win multiplier
    return ""; // Regular win - no special type
  }

  return ""; // Not a win
};

// Removed unused calculateWinAnimationThresholds function - replaced with simpler logic in WinCounter

/**
 * Get native dimensions of a symbol from spritesheet JSON data
 * @param {string} symbolName - Name of the symbol (e.g., "reel_symbol_1")
 * @param {Object} spriteSheetData - The parsed JSON data from the spritesheet
 * @returns {Object} - { width, height } of the symbol's native dimensions
 */
export const getSymbolNativeDimensions = (symbolName, spriteSheetData) => {
  const frameData = spriteSheetData?.frames?.[`${symbolName}.png`];
  if (!frameData) {
    // Fallback to common size if not found
    return { width: Settings.SYMBOL_WIDTH, height: Settings.SYMBOL_HEIGHT };
  }

  const { w, h } = frameData.sourceSize;
  return { width: w, height: h };
};

/**
 * Finds the highest bet amount that the user can afford with their current balance
 */
export const findHighestAffordableBet = (balance, bets) => {
  if (!bets || bets.length === 0 || balance < 0) {
    return null;
  }

  // Find the highest bet that is <= balance
  for (let i = bets.length - 1; i >= 0; i--) {
    if (bets[i] <= balance) {
      return bets[i];
    }
  }

  return null;
};

/**
 * Determines what action to take when user doesn't have enough balance for current bet
 */
export const checkBalanceAndBetStatus = (balance, currentBet, bets) => {
  // Check if user can afford ANY bet from the bets array
  const affordableBet = findHighestAffordableBet(balance, bets);

  // If no bet is affordable at all - trigger fake spin
  if (affordableBet === null) {
    return { action: "fake_spin" };
  }

  // Has credits and current bet is affordable
  if (balance >= currentBet) {
    return { action: "sufficient" };
  }

  // Has credits but not enough for current bet, but can afford a smaller bet
  return { action: "insufficient_credits", affordableBet };
};

/**
 * Find free spin intro animations from available spine animations
 * @param {string[]} availableAnimations - Array of available animation names from spine
 * @returns {Object} Object with inAnimation, idleAnimation, outAnimation properties
 */
export const getFreeSpinIntroAnimations = (availableAnimations) => {
  return {
    inAnimation: availableAnimations.find((anim) => anim === "fsin_in") || null,
    idleAnimation:
      availableAnimations.find((anim) => anim === "fsin_idle") || null,
    outAnimation:
      availableAnimations.find((anim) => anim === "fsin_out") || null,
  };
};

/**
 * Find free spin outro animations from available spine animations
 * @param {string[]} availableAnimations - Array of available animation names from spine
 * @returns {Object} Object with inAnimation, idleAnimation, outAnimation properties
 */
export const getFreeSpinOutroAnimations = (availableAnimations) => {
  return {
    inAnimation:
      availableAnimations.find((anim) => anim === "total_in") || null,
    idleAnimation:
      availableAnimations.find((anim) => anim === "total_idle") || null,
    outAnimation:
      availableAnimations.find((anim) => anim === "total_out") || null,
  };
};

/**
 * Find hold and win outro animations from available spine animations
 * @param {string[]} availableAnimations - Array of available animation names from spine
 * @returns {Object} Object with inAnimation, idleAnimation, outAnimation properties
 */
export const getHoldAndWinOutroAnimations = (availableAnimations) => {
  return {
    inAnimation: availableAnimations.find((anim) => anim === "in") || null,
    idleAnimation:
      availableAnimations.find((anim) => anim === "idle") || null,
    outAnimation: availableAnimations.find((anim) => anim === "out") || null,
  };
};

/**
 * Find free spin retrigger animation from available spine animations
 * @param {string[]} availableAnimations - Array of available animation names from spine
 * @returns {Object} Object with retriggerAnimation property
 */
export const getFreeSpinRetriggerAnimations = (availableAnimations) => {
  return {
    retriggerAnimation:
      availableAnimations.find((anim) => anim === "retrigger") || null,
  };
};

// Animation state management to prevent concurrent animations
export const activeAnimations = new Map();

/**
 * Helper to get animation key with type for concurrent animation support
 * Allows multiple animation types to run on the same sprite simultaneously
 * @param {PIXI.Sprite} sprite - The sprite to generate key for
 * @param {string} type - Animation type ('press' or 'rotation')
 * @returns {string} Unique key for this sprite + animation type combination
 */
const getAnimationKey = (sprite, type) => {
  if (!sprite._animationId) {
    sprite._animationId = Math.random().toString(36).substr(2, 9);
  }
  return `${sprite._animationId}_${type}`;
};

/**
 * Creates a reusable button press animation for PIXI sprites
 * @param {PIXI.Sprite} sprite - The sprite to animate
 * @param {Object} options - Animation options
 * @returns {anime} The anime.js animation instance
 */
export const createButtonPressAnimation = (sprite, options = {}) => {
  if (!sprite) return null;

  const animKey = getAnimationKey(sprite, "press");

  // Check if there's an existing PRESS animation currently active
  const existingAnimation = activeAnimations.get(animKey);
  if (existingAnimation) {
    existingAnimation.pause();
    // Immediately reset to original dimensions to prevent conflicts
    if (sprite._originalWidth !== undefined) {
      sprite.width = sprite._originalWidth;
      sprite.height = sprite._originalHeight;
    }
  }

  // Store the original width and height (before any animations)
  if (sprite._originalWidth === undefined) {
    sprite._originalWidth = sprite.width;
    sprite._originalHeight = sprite.height;
    sprite._originalEventMode = sprite.eventMode;
  }

  const originalWidth = sprite._originalWidth;
  const originalHeight = sprite._originalHeight;

  // Force reset to original dimensions at the start to prevent drift
  sprite.width = originalWidth;
  sprite.height = originalHeight;

  // Create press animation
  const animation = anime({
    targets: sprite,
    width: originalWidth * 0.95, // Scale down more noticeably
    height: originalHeight * 0.95,
    duration: 60, // Faster press effect
    easing: "easeInOutQuad",
    complete: function () {
      // Animate back to original dimensions
      anime({
        targets: sprite,
        width: originalWidth,
        height: originalHeight,
        duration: 60, // Faster return for better responsiveness
        easing: "easeInOutQuad", // Snappier easing for quick response
        complete: function () {
          // Ensure final dimensions are exactly correct
          sprite.width = originalWidth;
          sprite.height = originalHeight;

          // Clean up animation state
          activeAnimations.delete(animKey);

          // Clean up stored original values after animation completes
          const rotationKey = getAnimationKey(sprite, "rotation");
          if (!activeAnimations.has(rotationKey)) {
            delete sprite._originalWidth;
            delete sprite._originalHeight;
            delete sprite._originalEventMode;
          }
        },
      });
    },
  });

  // Mark animation start time for conflict detection
  animation._startTime = Date.now();

  // Track this animation with type-specific key
  activeAnimations.set(animKey, animation);

  return animation;
};

export const safeInit = (callback) => {
  requestAnimationFrame(() => {
    callback();
  });
};

/**
 * Scale up animation for Pixi containers
 * @param {PIXI.Container} target - The container to animate
 * @param {number} scaleTarget - Target scale value (default 1.4)
 * @param {number} duration - Animation duration in ms (default 150)
 * @returns {anime} The anime.js animation instance
 */
export const createScaleUpAnimation = (
  target,
  scaleTarget = 1.25,
  duration = 150
) => {
  if (!target) return null;

  return anime({
    targets: target.scale,
    x: scaleTarget,
    y: scaleTarget,
    duration: duration,
    easing: "easeInOutQuad",
  });
};

/**
 * Scale down animation for Pixi containers
 * @param {PIXI.Container} target - The container to animate
 * @param {number} duration - Animation duration in ms (default 175)
 * @param {function} onComplete - Optional callback to fire when animation completes
 * @returns {anime} The anime.js animation instance
 */
export const createScaleDownAnimation = (
  target,
  duration = 150,
  onComplete = null
) => {
  if (!target) return null;

  return anime({
    targets: target.scale,
    x: 1,
    y: 1,
    duration: duration,
    easing: "easeInOutQuad",
    complete: onComplete,
  });
};

/**
 * Creates a rotation animation for the play button (separate from scaling)
 * @param {PIXI.Sprite} sprite - The play button sprite to animate
 * @param {Object} options - Animation options
 * @returns {anime} The anime.js animation instance
 */
export const createPlayButtonRotationAnimation = (sprite, options = {}) => {
  if (!sprite) return null;

  const animKey = getAnimationKey(sprite, "rotation");

  // Check if there's an existing ROTATION animation currently active
  const existingAnimation = activeAnimations.get(animKey);
  if (existingAnimation) {
    existingAnimation.pause();
    // Immediately reset to original state to prevent conflicts
    if (sprite._originalRotation !== undefined) {
      sprite.rotation = 0; // Reset to 0 degrees
    }
  }

  // Store the original state (before any animations)
  if (sprite._originalRotation === undefined) {
    sprite._originalRotation = sprite.rotation;
    // sprite._originalEventMode = sprite.eventMode; // Not needed - we don't disable eventMode
  }

  // const originalEventMode = sprite._originalEventMode; // Not needed - we don't disable eventMode

  // Force reset to original state at the start to prevent drift
  sprite.rotation = 0; // Start from 0 degrees

  // Don't disable pointer events - allow clicking to stop during rotation animation
  // sprite.eventMode = "none";

  // Create the rotation animation sequence (no scaling)
  const animation = anime({
    targets: sprite,
    // Phase 1: Rotate to the right
    rotation: Math.PI / 2, // 90 degrees to the right
    duration: 142, // Faster
    easing: "easeOutQuad",
    complete: function () {
      // Phase 2: Rotate slightly backwards from initial position
      anime({
        targets: sprite,
        rotation: (-45 * Math.PI) / 180, // -45 degrees (in radians)
        duration: 142, // Faster
        easing: "easeOutQuad",
        complete: function () {
          // Phase 3: Return to start position
          anime({
            targets: sprite,
            rotation: 0, // Back to start position
            duration: 142, // Faster
            easing: "easeOutQuad",
            complete: function () {
              // Ensure final state is exactly correct
              sprite.rotation = 0; // Reset to 0 degrees
              // sprite.eventMode = originalEventMode; // Not needed since we don't disable it

              // Clean up animation state
              activeAnimations.delete(animKey);

              // Clean up stored original values after animation completes
              delete sprite._originalRotation;
              delete sprite._originalEventMode;

              // Only clean up dimensions if press animation is not using them
              const pressKey = getAnimationKey(sprite, "press");
              if (!activeAnimations.has(pressKey)) {
                delete sprite._originalWidth;
                delete sprite._originalHeight;
              }
            },
          });
        },
      });
    },
  });

  // Mark animation start time for conflict detection
  animation._startTime = Date.now();

  // Track this animation with type-specific key
  activeAnimations.set(animKey, animation);

  return animation;
};
